import { Component } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  ReceiptComponent,
  ReceiptDisplay,
} from '../../components/receipt/receipt.component';
import { MatDialog } from '@angular/material/dialog';
import { FileProcessorService } from '@app/shared/services/wasm/file-processor/file-processor.service';
import { NotificationService } from '@app/shared/services/notification/notification.service';
import { NotificationhubService } from '@app/shared/services/notificationhub/notificationhub.service';
import {
  IFilters,
  PermanentTablePaginatedComponent,
} from '../../components/permanent-table-paginated/permanent-table-paginated.component';
import { PrettyConsole } from '@app/shared/utils';
import { DataSource } from '@angular/cdk/collections';
import { PermanentTableComponent } from '../../components/permanent-table/permanent-table.component';
import {
  FileParameter,
  ILatamReceiptDto,
  LatamReceiptClient,
  LatamReceiptDto,
} from '@tech/api-clients/receipts';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-receipt-latam',
  standalone: true,
  imports: [ReceiptComponent, TranslateModule],
  templateUrl: './receipt-latam.component.html',
  styleUrl: './receipt-latam.component.scss',
})
export class ReceiptLatamComponent extends ReceiptComponent {
  override display_columns = [
    'file',
    'err_line',
    ...Object.keys(LatamReceiptDto).map((c) => c.toUpperCase()),
  ];
  override staged_receipts: ReceiptDisplay[] = [];
  override sse_name = 'LatamReceipt';
  override available_filters: IFilters[] = [];
  override upload_field_text = 'Latam Receipts';
  override validation_schema: {};
  override wasm_split_pattern: string = '';

  constructor(
    dialog: MatDialog,
    fileProcessor: FileProcessorService,
    notify: NotificationService,
    hub: NotificationhubService,
    private child_client: LatamReceiptClient
  ) {
    super(dialog, fileProcessor, notify, hub);
    this.sse_name = 'AccountReturn';
    this.validation_schema = {};
    this.upload_handler = this.upload_files_client.bind(this);
    this.lookupPermanent = this.look_today.bind(this);
    this.callme = this.lookup_all.bind(this);
    this.get_paginated_data = this.get_paginated_data.bind(this);
    this.child_client.getLatamFilterStructure(undefined).subscribe({
      next: (x) => {
        var parsed = JSON.parse(x);
        this.available_filters = parsed;
      },
      error: (e) => {
        PrettyConsole.ERROR(
          `Something failed while getting filter structure ${e}`
        );
      },
    });
  }

  upload_files_client = (files: FileParameter[]) => {
    return this.child_client.uploadLatamReceiptFiles(files);
  };

  look_today = () => {
    const loading$: BehaviorSubject<boolean> = new BehaviorSubject(true);
    const ds = new PermanentLatamReceipts();
    this.child_client.getLatamReceiptsUploadedToday(undefined).subscribe({
      next: (returns) => {
        ds.setData(returns);
        loading$.next(false);
      },
      error: (err) => {
        PrettyConsole.ERROR(
          `getAccountReturnsUploadedToday got an error on subscribe ${err}`
        );
        loading$.next(false);
      },
    });
    this.dialog.open(PermanentTableComponent<PermanentLatamReceipts>, {
      width: '85vw',
      height: '80vh',
      data: {
        title: 'Receipts of last remittance',
        receipts: ds,
        display: Object.keys(LatamReceiptDto.prototype.toJSON()),
        loading$,
        callback: this.lookup_all,
      },
    });
  };

  lookup_all = () => {
    this.dialog.closeAll();
    if (this.available_filters.length === 0) {
      this.child_client.getLatamFilterStructure(undefined).subscribe({
        next: (x) => {
          var parsed = JSON.parse(x);
          this.available_filters = parsed;
        },
        error: (e) => {
          PrettyConsole.ERROR(
            `Something failed while getting filter structure ${e}`
          );
        },
      });
    }

    const data_source = new PermanentLatamReceipts();
    this.dialog.open(PermanentTablePaginatedComponent<ILatamReceiptDto>, {
      width: '100vw',
      maxWidth: '98vw',
      height: '95vh',
      data: {
        receipts: data_source,
        display: Object.keys(LatamReceiptDto.prototype.toJSON()).filter(
          (d) => d !== 'id'
        ),
        callback: this.get_paginated_data,
        filters: this.available_filters,
      },
    });
  };

  override get_paginated_data = (
    page: number,
    size: number,
    filters: IFilters[]
  ) => {
    let data = new BehaviorSubject<LatamReceiptDto[]>([]);
    let count = new BehaviorSubject<number>(0);

    let filter_values: Record<string, any> = {};
    filters.forEach((f) => {
      filter_values[f.id] = f.value === null ? undefined : f.value;
    });

    this.child_client
      .getAllLatamReceipts(page, size, JSON.stringify(filter_values))
      .subscribe({
        error: (_) => {
          PrettyConsole.ERROR(
            `Error while getting the AccountReturn receipts. page: ${page}, size: ${size}`
          );
        },
        next: (d) => {
          if (d.count) count.next(d.count);
          if (d.receipts) data.next(d.receipts);
        },
      });
    return { count: count.asObservable(), data: data.asObservable() };
  };
}

export class PermanentLatamReceipts extends DataSource<ILatamReceiptDto> {
  private data = new BehaviorSubject<ILatamReceiptDto[]>([]);

  connect(): Observable<ILatamReceiptDto[]> {
    return this.data.asObservable();
  }

  addRow(row: ILatamReceiptDto) {
    const current = this.data.getValue();
    this.data.next([...current, row]);
  }

  setData(rows: LatamReceiptDto[]) {
    const data = rows.map((r) => r.toJSON());
    this.data.next(data);
  }

  disconnect(): void {}
}
